// Supplemental physician orders service (Phase 8 §3.9, ORD-09)
// File: src/services/patientmr/orders.service.ts

import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type {
  CreateOrderRequest,
  ListOrdersResponse,
  OrderDocument,
  OrderDocumentConfirmRequest,
  OrderDocumentUrlResponse,
  OrderPdfUrlResponse,
  OrderSentMethod,
  PhysicianOrder,
  UpdateOrderRequest,
} from '../../dtos/patientmr/orders.dto';
import { PresignedUploadService, ticketRequestFor } from '../uploads/presignedUpload';
import type { UploadTicket } from '../uploads/presignedUpload';

export const SIGNED_ORDER_DOCUMENT_TYPE = 'SIGNED_ORDER';

type OrderEnvelope = { order?: PhysicianOrder } & Partial<PhysicianOrder>;
const unwrapOrder = (data: OrderEnvelope): PhysicianOrder => (data.order ?? data) as PhysicianOrder;

export class OrdersService {
  static async list(patientId: string): Promise<PhysicianOrder[]> {
    const response: ApiResponse<ListOrdersResponse> = await apiClient.get(`/patientmr/${patientId}/orders`);
    if (response.success && response.data) {
      return response.data.orders || [];
    }
    throw new Error(response.error?.message || 'Failed to load orders');
  }

  static async create(patientId: string, body: CreateOrderRequest): Promise<PhysicianOrder> {
    const response: ApiResponse<OrderEnvelope> = await apiClient.post(`/patientmr/${patientId}/orders`, body);
    if (response.success && response.data) {
      return unwrapOrder(response.data);
    }
    throw new Error(response.error?.message || 'Failed to create the order');
  }

  static async update(patientId: string, orderId: string, body: UpdateOrderRequest): Promise<PhysicianOrder> {
    const response: ApiResponse<OrderEnvelope> = await apiClient.put(`/patientmr/${patientId}/orders/${orderId}`, body);
    if (response.success && response.data) {
      return unwrapOrder(response.data);
    }
    throw new Error(response.error?.message || 'Failed to update the order');
  }

  /** Phase 19 F20: record that the order was sent for signature (server defaults sentAt to now). */
  static async markOrderSent(
    patientId: string,
    orderId: string,
    sentMethod: OrderSentMethod,
    sentAt?: string
  ): Promise<PhysicianOrder> {
    return OrdersService.update(patientId, orderId, { sentMethod, ...(sentAt ? { sentAt } : {}) });
  }

  /** Phase 19 F20: 5-minute link to the printable order sheet PDF. */
  static async getOrderPdfUrl(patientId: string, orderId: string): Promise<OrderPdfUrlResponse> {
    const response: ApiResponse<OrderPdfUrlResponse> = await apiClient.get(
      `/patientmr/${patientId}/orders/${orderId}/pdf`
    );
    if (response.success && response.data?.url) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to prepare the order sheet');
  }

  static async getUploadTicket(patientId: string, orderId: string, file: File): Promise<UploadTicket> {
    const response: ApiResponse<UploadTicket> = await apiClient.post(
      `/patientmr/${patientId}/orders/${orderId}/documents/upload-url`,
      ticketRequestFor(file)
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to get an upload link');
  }

  /** Short-lived signed URL for one of the order's documents (e.g. the signed copy). */
  static async getOrderDocumentUrl(
    patientId: string,
    orderId: string,
    documentId: string
  ): Promise<OrderDocumentUrlResponse> {
    const response: ApiResponse<OrderDocumentUrlResponse> = await apiClient.get(
      `/patientmr/${patientId}/orders/${orderId}/documents/${documentId}/url`
    );
    if (response.success && response.data?.url) {
      return response.data;
    }
    throw new Error(response.error?.message || 'Failed to get the download link');
  }

  /** Confirm an uploaded copy. The server may return the document or the updated order. */
  static async confirmDocument(
    patientId: string,
    orderId: string,
    body: OrderDocumentConfirmRequest
  ): Promise<{ document?: OrderDocument; order?: PhysicianOrder }> {
    const response: ApiResponse<{ document?: OrderDocument; order?: PhysicianOrder } & Partial<OrderDocument>> = await apiClient.post(
      `/patientmr/${patientId}/orders/${orderId}/documents`,
      body
    );
    if (response.success && response.data) {
      const data = response.data;
      if (data.document || data.order) return { document: data.document, order: data.order };
      if (data.documentId) return { document: data as OrderDocument };
      return {};
    }
    throw new Error(response.error?.message || 'Failed to save the signed copy');
  }

  /**
   * Upload the physician-signed copy and mark the order SIGNED
   * (ticket → S3 → confirm → PUT signedDocumentId when the confirm didn't already sign it).
   */
  static async uploadSignedCopy(
    patientId: string,
    orderId: string,
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<PhysicianOrder | null> {
    const confirmed = await PresignedUploadService.uploadAndConfirm(
      file,
      () => OrdersService.getUploadTicket(patientId, orderId, file),
      (key) => OrdersService.confirmDocument(patientId, orderId, {
        key,
        documentName: file.name,
        documentType: SIGNED_ORDER_DOCUMENT_TYPE,
      }),
      onProgress
    );
    if (confirmed.order?.status === 'SIGNED') return confirmed.order;
    const documentId = confirmed.document?.documentId;
    if (!documentId) return confirmed.order ?? null;
    return OrdersService.update(patientId, orderId, { signedDocumentId: documentId });
  }
}

export default OrdersService;
